import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  StickyNote, Plus, Star, Archive, ArchiveRestore, Trash2, Search, Tag, X, Pencil,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { WsPageTitle } from "./WorkspaceLayout";
import WorkspaceEditorModal from "../../components/admin/WorkspaceEditorModal";
import { SingleRichEditor } from "../../components/admin/RichEditor";
import { Field } from "../../components/admin/Fields";
import CategoryManager, { CategoryBadge, CategorySelect, CategorySidebar, useCategories, matchCategoryFilter } from "../../components/workspace/CategoryManager";
import { Settings2 } from "lucide-react";
import { formatDMY } from "../../components/ui/datetime-picker";

function fmtDate(iso) {
  if (!iso) return "";
  return formatDMY(iso, true);
}
const empty = () => ({ id: null, title: "", body: "", tags: [], favorite: false, category: "" });

export default function Notes() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [active, setActive] = useState(empty());
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const { categories } = useCategories("notes");

  const { data = [] } = useQuery({
    queryKey: ["workspace", "notes", archived, favoriteOnly, q],
    queryFn: () => api.get("/admin/workspace/notes", {
      params: { archived: archived ? 1 : 0, favorite: favoriteOnly ? 1 : 0, q }
    }).then((r) => r.data),
  });

  // catFilter now holds a category ID; matchCategoryFilter handles descendants.
  const filtered = data.filter((n) => matchCategoryFilter(n.category, catFilter, categories));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["workspace", "notes"] });
    qc.invalidateQueries({ queryKey: ["workspace", "overview"] });
  };

  const openNew = () => { setActive(empty()); setDirty(false); setModalOpen(true); };
  const openEdit = (n) => { setActive({ ...empty(), ...n }); setDirty(false); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setDirty(false); };

  const setField = (k, v) => { setActive((a) => ({ ...a, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!active.title.trim() && !active.body.trim()) {
      toast.error("Başlık veya içerik boş olamaz");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: active.title, body: active.body,
        tags: active.tags, favorite: !!active.favorite,
        category: active.category || "",
      };
      if (active.id) {
        await api.put(`/admin/workspace/notes/${active.id}`, payload);
        toast.success("Not güncellendi");
      } else {
        await api.post("/admin/workspace/notes", payload);
        toast.success("Not oluşturuldu");
      }
      setDirty(false);
      invalidate();
      setModalOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const archive = async (id, restore = false) => {
    try {
      await api.post(`/admin/workspace/notes/${id}/archive`, null, { params: { restore: restore ? 1 : 0 } });
      toast.success(restore ? "Geri yüklendi" : "Arşive alındı");
      invalidate();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Not kalıcı olarak silinecek. Onaylıyor musunuz?")) return;
    try {
      await api.delete(`/admin/workspace/notes/${id}`);
      toast.success("Silindi");
      invalidate();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || active.tags.includes(t)) { setTagInput(""); return; }
    setActive((a) => ({ ...a, tags: [...a.tags, t] }));
    setTagInput("");
    setDirty(true);
  };

  // Rough excerpt for the card preview
  const excerpt = (html, max = 160) => {
    if (!html) return "";
    const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    return text.length > max ? text.slice(0, max) + "…" : text;
  };

  return (
    <div data-testid="ws-notes">
      <WsPageTitle
        icon={StickyNote}
        title="Notlar"
        sub="Hızlı kayıtların, günlüğün ve fikir toplama alanın."
        action={
          <>
            <Button variant="outline" size="sm" onClick={() => setCatMgrOpen(true)} data-testid="ws-note-cat-mgr">
              <Settings2 size={13} className="mr-1" /> Kategoriler
            </Button>
            <Button onClick={openNew} data-testid="ws-note-new"><Plus size={14} className="mr-1" />Yeni not</Button>
          </>
        }
      />

      {/* Sidebar + content */}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4" data-testid="ws-notes-layout">
        <CategorySidebar
          kind="notes"
          items={data}
          value={catFilter}
          onChange={setCatFilter}
          onManage={() => setCatMgrOpen(true)}
          accent="indigo"
          allLabel="Tüm Notlar"
        />

        <div className="min-w-0 space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-2xl border border-indigo-100 p-3 shadow-sm flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={13} className="absolute left-2.5 top-2.5 text-stone-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Notlarda ara..." className="pl-7 h-9 text-sm" data-testid="ws-note-search" />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
              <button onClick={() => setFavoriteOnly((v) => !v)} className={`px-2.5 py-1.5 rounded-full border ${favoriteOnly ? "bg-amber-50 border-amber-300 text-amber-800 font-semibold" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`} data-testid="ws-note-fav-filter">
                <Star size={10} className="inline mr-1" />Favori
              </button>
              <button onClick={() => setArchived((v) => !v)} className={`px-2.5 py-1.5 rounded-full border ${archived ? "bg-stone-100 border-stone-300 font-semibold" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`} data-testid="ws-note-archived-filter">
                <Archive size={10} className="inline mr-1" />{archived ? "Arşiv" : "Aktif"}
              </button>
            </div>
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-indigo-200 p-10 text-center text-stone-500">
              <StickyNote size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Henüz not yok.</p>
              <Button onClick={openNew} className="mt-3" size="sm"><Plus size={13} className="mr-1" />İlk notunu oluştur</Button>
            </div>
          ) : (
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ws-notes-grid">
              {filtered.map((n) => (
                <li key={n.id}
                    className="bg-white rounded-xl border border-indigo-100 shadow-sm hover:shadow-md transition p-3.5 flex flex-col group"
                    data-testid={`ws-note-item-${n.id}`}>
                  <div className="flex items-start gap-1.5 mb-1.5">
                    {n.favorite && <Star size={13} className="text-amber-500 fill-amber-500 shrink-0 mt-0.5" />}
                    <h3 className="text-sm font-semibold text-slate-900 line-clamp-2 flex-1">{n.title || "(başlıksız)"}</h3>
                  </div>
                  <p className="text-xs text-stone-600 line-clamp-4 flex-1">{excerpt(n.body)}</p>
                  {n.tags?.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-2">
                      {n.tags.slice(0, 3).map((t, i) => (
                        <span key={i} className="text-[10px] bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-full px-1.5 py-0.5">#{t}</span>
                      ))}
                    </div>
                  )}
                  {n.category && (
                    <div className="mt-1.5">
                      <CategoryBadge name={n.category} color={categories.find((c) => c.name === n.category)?.color} />
                    </div>
                  )}
                  <p className="text-[11px] text-stone-400 mt-2">{fmtDate(n.updated_at)}</p>
                  <div className="flex items-center gap-1 mt-2 pt-2 border-t">
                    <Button size="sm" variant="outline" onClick={() => openEdit(n)} data-testid={`ws-note-edit-${n.id}`}><Pencil size={11} className="mr-1" />Düzenle</Button>
                    {!archived ? (
                      <Button size="sm" variant="outline" onClick={() => archive(n.id)} title="Arşive al"><Archive size={11} /></Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => archive(n.id, true)} title="Geri yükle"><ArchiveRestore size={11} /></Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => remove(n.id)} className="text-red-700 ml-auto" title="Sil"><Trash2 size={11} /></Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Modal */}
      <WorkspaceEditorModal
        open={modalOpen}
        onClose={closeModal}
        title={active.id ? "Notu düzenle" : "Yeni not"}
        icon={StickyNote}
        size="lg"
        busy={busy}
        dirty={dirty}
        onSubmit={save}
        submitLabel={active.id ? "Güncelle" : "Oluştur"}
        testid="ws-note-modal"
        accent="indigo"
      >
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <Input
              value={active.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Not başlığı..."
              className="text-base font-semibold flex-1"
              data-testid="ws-note-title"
            />
            <button
              type="button"
              onClick={() => setField("favorite", !active.favorite)}
              className={`shrink-0 p-2 rounded-full border ${active.favorite ? "text-amber-500 bg-amber-50 border-amber-200" : "text-stone-300 border-stone-200 hover:text-amber-400"}`}
              title="Favori"
              data-testid="ws-note-fav-toggle"
            >
              <Star size={16} className={active.favorite ? "fill-amber-500" : ""} />
            </button>
          </div>

          <SingleRichEditor
            value={active.body}
            onChange={(v) => setField("body", v)}
            testid="ws-note-body"
            minH="min-h-[280px]"
          />

          <Field label="Kategori">
            <CategorySelect
              value={active.category}
              onChange={(v) => setField("category", v)}
              kind="notes"
              testid="ws-note-category"
              onManage={() => setCatMgrOpen(true)}
            />
          </Field>

          <Field label="Etiketler">
            <div className="flex items-center gap-1 flex-wrap p-2 border rounded-md bg-white min-h-[42px]">
              {active.tags.map((t, i) => (
                <span key={i} className="text-[11px] bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                  #{t}
                  <button type="button" onClick={() => { setActive((a) => ({ ...a, tags: a.tags.filter((_, j) => j !== i) })); setDirty(true); }} className="hover:text-red-600"><X size={10} /></button>
                </span>
              ))}
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="etiket ekle…"
                className="text-[12px] outline-none bg-transparent flex-1 min-w-[100px]"
                data-testid="ws-note-tag-input"
              />
            </div>
          </Field>
        </div>
      </WorkspaceEditorModal>

      <CategoryManager
        open={catMgrOpen}
        onClose={() => setCatMgrOpen(false)}
        kind="notes"
        title="Not kategorilerini yönet"
      />
    </div>
  );
}
